<template>
  <v-app>
    <div class="taxonomy-editor-container">
      <div class="flex-container">
        <SidebarControls
          :search-query="searchQuery"
          @update:searchQuery="searchQuery = $event"
          :current-view="currentView"
          @update:currentView="handleViewChangeRequest"
          :load-version="loadVersion"
          @update:loadVersion="onLoadVersionChange"
          :formatted-versions="formattedVersions"
          :show-backup-versions="showBackupVersions"
          @toggleShowBackupVersions="showBackupVersions = !showBackupVersions"
          :save-version="saveVersion"
          @update:saveVersion="saveVersion = $event"
          :version-description="versionDescription"
          @update:versionDescription="versionDescription = $event"
          :current-version-description="currentVersionDescription"
          :loaded-version="loadedVersion"
          :has-unsaved-changes="hasUnsavedChanges"
          :selected-taxonomy="selectedTaxonomy"
          :backend-taxonomy-name="getBackendTaxonomyDisplayName()"
          :taxonomy-version="getCurrentTaxonomyVersion()"
          :unsaved-changes="unsavedChanges"
          :orphan-count="orphanReport?.summary?.orphaned || 0"
          :system-health="systemHealth"
          :frontend-filename="getLinkedFrontendFilename()"
          :v2-endpoints-enabled="v2EndpointsEnabled"
          @navigate="$emit('navigate', $event)"
          @refreshVersions="loadTaxonomyVersions"
          @loadTaxonomy="loadTaxonomyFromCloud"
          @saveChanges="saveChanges"
          @saveFrontendChanges="saveFrontendChanges"
          @publishTaxonomy="openPublishDialog"
          @saveTaxonomy="saveChanges"
          @saveAttributes="saveChanges"
          @saveGpcMappings="saveGpcMappings"
          @downloadTaxonomy="downloadTaxonomy"
          @syncQaVersion="syncQaVersion"
        />

        <div class="main-content" style="flex-grow: 1">
          <!-- Active Sessions Warning Banner -->
          <v-alert
            v-if="activeSessions.length > 0"
            type="warning"
            variant="tonal"
            density="compact"
            closable
            class="alert-banner ma-2"
          >
            <v-icon start>mdi-account-multiple</v-icon>
            <strong>{{ activeSessions.length }} other session(s)</strong> active on this taxonomy
            <span v-if="activeSessions.some(s => s.hasUnsavedChanges)" class="ml-2">
              (some have unsaved changes)
            </span>
            <template v-slot:append>
              <v-chip 
                v-for="session in activeSessions.slice(0, 3)" 
                :key="session.sessionId"
                size="x-small"
                class="ml-1"
                :color="session.hasUnsavedChanges ? 'warning' : 'default'"
              >
                {{ session.activeSection?.replace('TaxonomyEditor', '') || 'Unknown' }}
              </v-chip>
            </template>
          </v-alert>

          <div class="selectors-section">
            <div
              class="selectors-header"
              @click="selectorsCollapsed = !selectorsCollapsed"
            >
              <v-btn
                icon
                small
                @click.stop="selectorsCollapsed = !selectorsCollapsed"
              >
                <v-icon>{{
                  selectorsCollapsed ? "mdi-chevron-down" : "mdi-chevron-up"
                }}</v-icon>
              </v-btn>
              <span class="ml-2">
                {{ selectorsCollapsed ? "Show" : "Hide" }} Navigation
                <span v-if="selectorsCollapsed" class="text-caption ml-2">
                  ({{ selectedTaxonomy?.replace(".json", "") }} →
                  {{ selectedTopLevelNode }})
                </span>
              </span>
            </div>

            <v-expand-transition>
              <div v-show="!selectorsCollapsed">
                
                <!-- V2 TAXONOMY SELECTOR (Enhanced) -->
                <div class="taxonomy-selector-section" v-if="v2EndpointsEnabled">
                  <!-- Current Taxonomy Banner -->
                  <div class="current-taxonomy-banner" :class="getTaxonomyVersionClass()">
                    <div class="d-flex align-center justify-space-between">
                      <div class="d-flex align-center gap-3">
                        <v-icon :color="getCurrentTaxonomyVersion() === 'v2' ? 'warning' : 'success'">
                          {{ getCurrentTaxonomyVersion() === 'v2' ? 'mdi-flask' : 'mdi-check-circle' }}
                        </v-icon>
                        <div>
                          <div class="taxonomy-name">{{ selectedTaxonomy?.replace('.json', '').replace(/_/g, ' ') }}</div>
                          <div class="taxonomy-version-label">
                            {{ getCurrentTaxonomyVersion() === 'v2' ? 'V2 - In Development' : 'V1 - Current Production' }}
                          </div>
                        </div>
                      </div>
                      
                      <div class="d-flex align-center gap-2">
                        <!-- Taxonomy Family Selector - only shown in backend views -->
                        <v-select
                          v-if="currentView !== 'frontendTaxonomyEditor'"
                          v-model="selectedTaxonomy"
                          :items="formattedTaxonomiesGrouped"
                          item-title="displayName"
                          item-value="filename"
                          label="Taxonomy"
                          density="compact"
                          variant="outlined"
                          style="min-width: 280px"
                          hide-details
                        >
                          <template v-slot:item="{ item, props }">
                            <v-list-item v-bind="props">
                              <template v-slot:prepend>
                                <v-icon 
                                  :color="item.raw.isV2 ? 'warning' : 'success'" 
                                  size="small"
                                >
                                  {{ item.raw.isV2 ? 'mdi-flask' : 'mdi-check-circle' }}
                                </v-icon>
                              </template>
                              <template v-slot:append v-if="item.raw.hasProduction">
                                <v-chip size="x-small" color="success" variant="tonal">PROD</v-chip>
                              </template>
                            </v-list-item>
                          </template>
                        </v-select>
                        
                        <!-- Frontend view: show linked frontend label instead of dropdown -->
                        <v-chip 
                          v-if="currentView === 'frontendTaxonomyEditor'"
                          color="info"
                          variant="tonal"
                        >
                          <v-icon start size="small">mdi-link</v-icon>
                          {{ getLinkedFrontendFilename() }} (linked to {{ selectedBackendTaxonomy?.replace('.json', '').replace(/_/g, ' ') || 'backend' }})
                        </v-chip>
                        
                        <!-- Create V2 Button (only if V2 doesn't exist and endpoints enabled) -->
                        <v-btn
                          v-if="v2EndpointsEnabled && !hasV2Taxonomy() && getCurrentTaxonomyVersion() === 'v1' && currentView !== 'frontendTaxonomyEditor'"
                          color="primary"
                          variant="outlined"
                          size="small"
                          @click="showCreateV2Dialog = true"
                        >
                          <v-icon start>mdi-plus</v-icon>
                          Create V2
                        </v-btn>
                      </div>
                    </div>
                  </div>
                  
                  <!-- V2 Orphan Warning (if applicable) -->
                  <v-alert
                    v-if="getCurrentTaxonomyVersion() === 'v2' && orphanReport?.summary?.orphaned > 0"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mt-2"
                  >
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <strong>{{ orphanReport.summary.orphaned }} orphaned frontend rules</strong>
                        <div class="text-caption">
                          These rules reference V1 categories that don't exist in V2 yet.
                        </div>
                      </div>
                      <div class="d-flex align-center gap-2">
                        <v-progress-linear
                          :model-value="orphanReport.summary.percentResolved"
                          color="success"
                          height="8"
                          style="width: 100px"
                          rounded
                        />
                        <span class="text-caption">{{ orphanReport.summary.percentResolved }}% resolved</span>
                        <v-btn size="small" variant="text" @click="showOrphanReportDialog = true">
                          View Report
                        </v-btn>
                      </div>
                    </div>
                  </v-alert>
                </div>

                <div
                  class="tabs-row"
                  v-if="
                    topLevelNodes.length &&
                    currentView === 'backendTaxonomyEditor'
                  "
                >
                  <!-- Header Section -->
                  <div class="category-section-header">
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                      "
                    >
                      <h3 class="category-section-title">
                        Categories ({{ topLevelNodesWithBrickIds.length }})
                      </h3>
                      <div style="display: flex; gap: 8px; align-items: center">
                        <v-chip
                          :color="gpcSystemStatus.color"
                          size="small"
                          variant="outlined"
                        >
                          {{ gpcSystemStatus.text }}
                        </v-chip>
                        <template v-if="debugMode">
                          <v-btn
                            size="small"
                            color="primary"
                            variant="outlined"
                            :loading="gpcDataLoading"
                            @click="initializeGpcMappingWithAutoSuggestions"
                          >
                            Initialize GPC System
                          </v-btn>
                          <v-btn
                            size="small"
                            color="secondary"
                            variant="outlined"
                            @click="testGpcApiManually"
                          >
                            Test API
                          </v-btn>
                        </template>
                      </div>
                    </div>
                  </div>

                  <!-- Category Grid -->
                  <v-row class="category-grid">
                    <v-col
                      v-for="node in topLevelNodesWithBrickIds"
                      :key="node.name"
                      cols="12"
                      sm="6"
                      md="4"
                      lg="3"
                      xl="2"
                    >
                      <v-card
                        :class="{
                          'category-card': true,
                          'category-card-active':
                            selectedTopLevelNode === node.name,
                        }"
                        @click="showCategoryDetails(node.name, node.brickId)"
                        hover
                        elevation="2"
                      >
                        <v-card-text class="pa-2">
                          <div class="d-flex flex-column gap-1">
                            <div
                              class="category-name text-body-2 font-weight-medium"
                            >
                              {{ node.name }}
                            </div>
                            <v-chip
                              v-if="node.brickId"
                              size="x-small"
                              color="success"
                              variant="tonal"
                              class="align-self-start"
                              style="max-width: 100%; word-break: break-all"
                              :title="node.brickId"
                            >
                              <v-icon start size="x-small"
                                >mdi-check-circle</v-icon
                              >
                              {{
                                node.brickId.length > 20
                                  ? node.brickId.substring(0, 20) + "..."
                                  : node.brickId
                              }}
                            </v-chip>
                            <v-chip
                              v-else
                              size="x-small"
                              color="warning"
                              variant="tonal"
                              class="align-self-start"
                            >
                              <v-icon start size="x-small"
                                >mdi-alert-circle</v-icon
                              >
                              No Mapping
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-expand-transition>
          </div>

          <div
            v-if="
              currentView === 'backendTaxonomyEditor' ||
              currentView === 'frontendTaxonomyEditor'
            "
            class="view-content"
          >
            <div class="attribute-management">
              <div class="sticky-header">
                <div
                  class="d-flex align-items-center justify-space-between mb-3"
                >
                  <div>
                    <h2 class="view-title mb-1">
                      <v-icon class="mr-2" color="primary">
                        {{
                          currentView === "backendTaxonomyEditor"
                            ? "mdi-database"
                            : "mdi-web"
                        }}
                      </v-icon>
                      <span v-if="currentView === 'backendTaxonomyEditor'"
                        >Backend Taxonomy Editor</span
                      >
                      <span v-else-if="currentView === 'frontendTaxonomyEditor'"
                        >Frontend Editor</span
                      >
                    </h2>

                    <!-- Breadcrumb and Stats -->
                    <div class="d-flex align-items-center gap-3">
                      <v-chip
                        size="small"
                        color="info"
                        variant="outlined"
                        v-if="selectedNode"
                      >
                        <v-icon start size="small">mdi-sitemap</v-icon>
                        {{ selectedNode.name }}
                      </v-chip>
                      <v-chip
                        size="small"
                        color="success"
                        variant="outlined"
                        v-if="
                          selectedNode && Object.keys(editableFilters).length
                        "
                      >
                        <v-icon start size="small">mdi-tag-multiple</v-icon>
                        {{ Object.keys(editableFilters).length }} attributes
                      </v-chip>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="d-flex gap-2">
                    <v-btn
                      size="small"
                      variant="outlined"
                      color="secondary"
                      @click="exportCategoriesToCSV"
                    >
                      <v-icon start size="small">mdi-download</v-icon>
                      Export
                    </v-btn>
                    <v-btn
                      size="small"
                      variant="outlined"
                      color="warning"
                      @click="openResetDialog"
                    >
                      <v-icon start size="small">mdi-refresh</v-icon>
                      Reset
                    </v-btn>
                  </div>
                </div>

                <template v-if="currentView === 'frontendTaxonomyEditor'">
                  <v-alert
                    type="info"
                    density="compact"
                    variant="tonal"
                    class="mb-3"
                  >
                    <template #text>
                      This is the generated frontend taxonomy. Right-click nodes
                      to view or edit their derivation rules.
                    </template>
                  </v-alert>
                </template>
              </div>
              <div class="panes-container">
                <TaxonomyTree
                  :key="treeRenderKey"
                  :org-data="orgData"
                  :horizontal="horizontal"
                  :collapsable="collapsable"
                  :left-pane-width="leftPaneWidth"
                  :dragged-node-id="draggedNodeId"
                  :highlighted-node-id="highlightedNodeId"
                  :source-colors="sourceColors"
                  :node-data-sources="nodeDataSources"
                  :custom-menus="customMenus"
                  @update:horizontal="horizontal = $event"
                  @update:collapsable="collapsable = $event"
                  @nodeClick="onNodeClick"
                  @contextAction="onContextMenuAction"
                  @expandChange="onExpandChange"
                  @dragStart="onNodeDragStart"
                  @dragEnd="onNodeDragEnd"
                  @dragOver="onNodeDragOver"
                  @dragLeave="onNodeDragLeave"
                  @filterDrop="onFilterDrop"
                />

                <div class="divider" @mousedown.prevent="startDrag"></div>

                <div class="pane-right">
                  <template v-if="!selectedNode">
                    <div class="text-center py-8">
                      <v-icon size="64" color="grey-lighten-1" class="mb-4"
                        >mdi-cursor-default-click</v-icon
                      >
                      <h3 class="text-h6 text-grey-darken-1 mb-2">
                        No Node Selected
                      </h3>
                      <p class="text-body-2 text-grey">
                        Select a node from the tree to view and edit its
                        properties and attributes
                      </p>
                    </div>
                  </template>

                  <template v-else>
                    <!-- Node Info Card -->
                    <v-card class="mb-4" variant="outlined">
                      <v-card-title class="d-flex align-items-center py-3">
                        <v-icon class="mr-2" color="primary">mdi-tag</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-h6">{{ selectedNode.name }}</div>
                          <div class="text-caption text-grey">
                            Node Properties
                          </div>
                        </div>
                        <v-btn
                          icon
                          size="small"
                          variant="text"
                          @click="openCategoryNotesDialog(selectedNode)"
                          title="Add Notes"
                        >
                          <v-icon>mdi-note-edit</v-icon>
                        </v-btn>
                      </v-card-title>

                      <v-card-text class="pt-0">
                        <v-row dense>
                          <v-col
                            cols="12"
                            v-if="getBrickIdForCategory(selectedNode.name)"
                          >
                            <div
                              class="d-flex align-items-center justify-space-between"
                            >
                              <span class="text-body-2 font-weight-medium"
                                >Brick ID:</span
                              >
                              <div class="d-flex align-items-center gap-2">
                                <code
                                  class="text-caption bg-grey-lighten-4 pa-1 rounded"
                                  >{{
                                    getBrickIdForCategory(selectedNode.name)
                                  }}</code
                                >
                                <v-btn
                                  icon
                                  size="x-small"
                                  variant="text"
                                  @click="
                                    copyToClipboard(
                                      getBrickIdForCategory(selectedNode.name)
                                    )
                                  "
                                  title="Copy Brick ID"
                                >
                                  <v-icon size="small">mdi-content-copy</v-icon>
                                </v-btn>
                              </div>
                            </div>
                          </v-col>
                          <v-col cols="6">
                            <div class="text-body-2 font-weight-medium mb-1">
                              Attributes
                            </div>
                            <v-chip
                              size="small"
                              color="primary"
                              variant="tonal"
                            >
                              {{ Object.keys(editableFilters).length }}
                            </v-chip>
                          </v-col>
                          <v-col cols="6">
                            <div class="text-body-2 font-weight-medium mb-1">
                              Type
                            </div>
                            <v-chip
                              size="small"
                              :color="
                                selectedNode.isDerived ? 'info' : 'success'
                              "
                              variant="tonal"
                            >
                              <v-icon start size="small">{{
                                selectedNode.isDerived
                                  ? "mdi-auto-fix"
                                  : "mdi-folder"
                              }}</v-icon>
                              {{
                                selectedNode.isDerived ? "Derived" : "Manual"
                              }}
                            </v-chip>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>

                    <!-- UPDATED: Web Category Codes Display for Frontend View (READ-ONLY) -->
                    <div
                      v-if="currentView === 'frontendTaxonomyEditor'"
                      class="mb-4"
                    >
                      <v-divider class="mb-4"></v-divider>
                      <div class="text-subtitle-1 font-weight-medium mb-1">
                        Web Category Codes
                      </div>
                      <p class="text-caption text-grey-darken-1 mb-2">
                        These codes are defined in the category rule. Use "Edit
                        Rule" to modify them.
                      </p>

                      <!-- Display multiple codes as chips if they exist -->
                      <div
                        v-if="
                          webCategoryCodesForSelectedNode &&
                          webCategoryCodesForSelectedNode.length > 0
                        "
                        class="mb-2"
                      >
                        <v-chip
                          v-for="code in webCategoryCodesForSelectedNode"
                          :key="code"
                          class="mr-2 mb-1"
                          size="small"
                          color="primary"
                          variant="outlined"
                        >
                          {{ code }}
                        </v-chip>
                      </div>

                      <!-- Fallback text field for when no codes exist -->
                      <v-text-field
                        v-else
                        model-value="(not set)"
                        label="Web Category Codes"
                        readonly
                        hide-details
                        density="compact"
                        variant="outlined"
                        class="readonly-field"
                      ></v-text-field>

                      <v-divider class="mt-4"></v-divider>
                    </div>
                    <!-- END UPDATED -->

                    <!-- Attributes Section -->
                    <v-card variant="outlined">
                      <v-card-title
                        class="d-flex align-items-center justify-space-between py-3"
                      >
                        <div class="d-flex align-items-center">
                          <v-icon class="mr-2" color="primary"
                            >mdi-tag-multiple</v-icon
                          >
                          <div>
                            <div class="text-h6">Attributes</div>
                            <div class="text-caption text-grey">
                              {{ Object.keys(editableFilters).length }} total
                            </div>
                          </div>
                        </div>
                        <v-btn
                          v-if="currentView !== 'frontendTaxonomyEditor'"
                          size="small"
                          color="primary"
                          variant="outlined"
                          @click="attributeSearchDialog.show = true"
                        >
                          <v-icon start size="small">mdi-plus</v-icon>
                          Add Attribute
                        </v-btn>
                      </v-card-title>

                      <v-divider></v-divider>

                      <v-card-text class="pa-4">
                        <div
                          class="d-flex align-items-center justify-space-between mb-3"
                        >
                          <h4 class="text-subtitle-1">
                            <v-icon class="mr-1" size="small"
                              >mdi-tag-multiple</v-icon
                            >
                            Attributes
                          </h4>
                          <v-chip
                            size="small"
                            color="info"
                            variant="outlined"
                            v-if="Object.keys(editableFilters).length"
                          >
                            {{ Object.keys(editableFilters).length }} total
                          </v-chip>
                        </div>

                        <div
                          v-if="
                            currentView === 'frontendTaxonomyEditor' &&
                            Object.keys(editableFilters).length > 0
                          "
                          class="mb-3"
                        >
                          <v-alert
                            type="info"
                            density="compact"
                            variant="tonal"
                          >
                            <template #text>
                              Attributes shown are derived from backend
                              categories and conditions. Use "Edit Rule" to
                              modify the category definition.
                            </template>
                          </v-alert>
                        </div>

                        <div
                          class="add-existing-filter-row"
                          style="
                            display: flex;
                            align-items: center;
                            gap: 0.5rem;
                            margin-bottom: 1rem;
                          "
                          v-if="currentView !== 'frontendTaxonomyEditor'"
                        >
                          <v-autocomplete
                            v-model="addExistingFilterName"
                            :items="allFilterNames"
                            item-title="title"
                            item-value="value"
                            label="Add existing attribute"
                            hide-details
                            clearable
                            placeholder="Type or select attribute"
                            @update:modelValue="onExistingFilterChange"
                            style="max-width: 250px"
                            :disabled="
                              currentView === 'frontendTaxonomyEditor' ||
                              (selectedNode &&
                                selectedNode.isDerived &&
                                selectedNode.backend_source_path &&
                                selectedNode.include_attributes)
                            "
                          ></v-autocomplete>
                        </div>

                        <!-- This condition is now more direct and robust -->
                        <template
                          v-if="Object.keys(editableFilters).length > 0"
                        >
                          <v-list dense style="padding: 0">
                            <v-list-item-group>
                              <div
                                v-for="(filterData, attrId) in editableFilters"
                                :key="attrId"
                                class="filter-container"
                              >
                                <v-list-item style="padding: 0.5rem">
                                  <v-list-item-content
                                    style="
                                      display: flex;
                                      align-items: center;
                                      gap: 0.5rem;
                                    "
                                  >
                                    <v-chip
                                      class="filter-name"
                                      :draggable="true"
                                      title="Drag Filter to Node to Copy"
                                      @dragstart="
                                        onFilterDragStart($event, attrId)
                                      "
                                    >
                                      <span
                                        v-if="editingFilterName !== attrId"
                                        @dblclick="editingFilterName = attrId"
                                      >
                                        {{ filterData.displayName }}
                                        <span class="attribute-name-suffix" v-if="getAttributeInternalName(attrId)">
                                          [{{ getAttributeInternalName(attrId) }}]
                                        </span>
                                      </span>
                                      <v-text-field
                                        v-else
                                        v-model="filterData.displayName"
                                        dense
                                        hide-details
                                        single-line
                                        autofocus
                                        @blur="saveFilterName(attrId)"
                                        @keydown.enter="saveFilterName(attrId)"
                                      ></v-text-field>
                                    </v-chip>
                                    <div
                                      class="source-chips-row"
                                      v-if="
                                        getFilterNameSourcesForSelectedNode(
                                          attrId
                                        ).length > 0
                                      "
                                    >
                                      <span
                                        v-for="src in getFilterNameSourcesForSelectedNode(
                                          attrId
                                        )"
                                        :key="src"
                                        class="source-dot"
                                        :style="{
                                          backgroundColor:
                                            sourceColors[src] || '#AAA',
                                        }"
                                        :title="src"
                                      />
                                    </div>
                                    <v-switch
                                      label="Required"
                                      class="required-switch"
                                      density="compact"
                                      color="green"
                                      hide-details
                                      v-model="selectedNode.metadata[attrId].is_required"></v-switch>
                                  </v-list-item-content>

                                  <v-list-item-icon>
                                    <v-icon
                                      small
                                      @click="toggleFilter(attrId)"
                                      style="cursor: pointer"
                                    >
                                      {{
                                        expandedFilters.includes(attrId)
                                          ? "mdi-chevron-up"
                                          : "mdi-chevron-down"
                                      }}
                                    </v-icon>
                                  </v-list-item-icon>
                                  <v-list-item-icon
                                    v-if="
                                      currentView !== 'frontendTaxonomyEditor'
                                    "
                                  >
                                    <v-icon
                                      small
                                      color="error"
                                      @click="removeFilter(attrId)"
                                      style="cursor: pointer"
                                    >
                                      mdi-delete
                                    </v-icon>
                                  </v-list-item-icon>
                                </v-list-item>

                                <div
                                  v-if="expandedFilters.includes(attrId)"
                                  class="filter-values-container"
                                >
                                  <v-simple-table>
                                    <thead>
                                      <tr>
                                        <th style="width: 70%">Values</th>
                                        <th style="width: 30%">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td colspan="2">
                                          <div class="values-row">
                                            <div
                                              v-for="(
                                                val, idx
                                              ) in filterData.values"
                                              :key="idx"
                                              class="value-item"
                                            >
                                              <!-- THIS CHIP IS NOW FULLY CORRECTED -->
                                              <v-chip
                                                class="value-chip"
                                                small
                                                :style="
                                                  getValueChipStyle(
                                                    filterData.displayName,
                                                    val
                                                  )
                                                "
                                                @contextmenu.prevent="
                                                  showValueContextMenu(
                                                    $event,
                                                    attrId,
                                                    val,
                                                    idx
                                                  )
                                                "
                                              >
                                                {{ val }}
                                                <v-icon
                                                  small
                                                  class="delete-icon"
                                                  @click.stop="
                                                    removeFilterValue(
                                                      attrId,
                                                      idx
                                                    )
                                                  "
                                                  :disabled="
                                                    currentView ===
                                                      'frontendTaxonomyEditor' ||
                                                    (selectedNode &&
                                                      selectedNode.isDerived &&
                                                      selectedNode.backend_source_path &&
                                                      selectedNode.include_attributes)
                                                  "
                                                >
                                                  mdi-close
                                                </v-icon>
                                              </v-chip>
                                            </div>
                                            <div
                                              v-if="
                                                filterData.values.length > 7
                                              "
                                              style="margin-top: 4px"
                                            >
                                              <v-btn
                                                text
                                                small
                                                @click="
                                                  toggleExpandValues({
                                                    attribute_id: attrId,
                                                  })
                                                "
                                              >
                                                {{
                                                  expandedAttributeValues[
                                                    attrId
                                                  ]
                                                    ? "Collapse"
                                                    : "Expand"
                                                }}
                                              </v-btn>
                                            </div>
                                            <div
                                              class="add-value"
                                              v-if="
                                                currentView !==
                                                  'frontendTaxonomyEditor' &&
                                                (!selectedNode ||
                                                  !selectedNode.isDerived ||
                                                  !selectedNode.backend_source_path ||
                                                  !selectedNode.include_attributes)
                                              "
                                            >
                                              <v-text-field
                                                v-model="
                                                  newFilterValues[attrId]
                                                "
                                                hide-details
                                                placeholder="Add value"
                                                style="max-width: 150px"
                                                @keydown.enter="
                                                  addFilterValue(attrId)
                                                "
                                              ></v-text-field>
                                              <v-icon
                                                small
                                                @click="addFilterValue(attrId)"
                                                style="cursor: pointer"
                                              >
                                                mdi-plus
                                              </v-icon>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </v-simple-table>
                                </div>
                              </div>
                            </v-list-item-group>
                          </v-list>
                        </template>

                        <template v-else>
                          <p>No Attributes Found</p>
                        </template>
                      </v-card-text>
                    </v-card>
                  </template>
                </div>
              </div>
              <div
                style="
                  margin-top: 1rem;
                  display: flex;
                  justify-content: flex-end;
                  gap: 0.5rem;
                "
              >
                <v-btn
                  color="warning"
                  @click="openResetDialog"
                  style="height: 36px"
                >
                  Reset Taxonomy
                </v-btn>
                <v-btn
                  color="secondary"
                  @click="exportCategoriesToCSV"
                  style="height: 36px"
                >
                  Download Categories
                </v-btn>
              </div>
            </div>
          </div>
          <div
            v-else-if="currentView === 'attributeOverview'"
            class="view-content"
          >
            <div class="attribute-management">
              <div class="sticky-header">
                <h2 class="view-title">Attribute Overview</h2>
              </div>

              <v-card class="pa-4 mb-4 elevation-1">
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-label">Total Attributes</div>
                    <div class="stat-value">
                      {{ attributeOverviewStats.totalAttributes }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-label">Distinct Names</div>
                    <div class="stat-value">
                      {{
                        Object.keys(attributeOverviewStats.nameCounts).length
                      }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-label">Avg. Attributes per Category</div>
                    <div class="stat-value">
                      {{ attributeOverviewStats.avgAttrsPerCategory }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-label">Avg. Categories per Attribute</div>
                    <div class="stat-value">
                      {{ attributeOverviewStats.avgCatsPerAttribute }}
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-label">Avg. # of Values per Attribute</div>
                    <div class="stat-value">
                      {{ attributeOverviewStats.avgValuesPerFilter }}
                    </div>
                  </v-col>
                </v-row>
              </v-card>

              <v-card class="pa-4 elevation-1">
                <v-card-title>All Attribute Names (alphabetical)</v-card-title>
                <v-card-text>
                  <div class="chip-container">
                    <v-chip
                      v-for="([name, count], index) in sortedNameEntries"
                      :key="index"
                      class="value-chip attr-name-chip"
                      small
                      @click="goToAttributeEditorByName(name)"
                    >
                      <v-icon
                        small
                        class="delete-icon"
                        @click.stop="removeFilterValue(attrId, idx)"
                        v-if="currentView !== 'frontendTaxonomyEditor'"
                        :disabled="
                          currentView === 'frontendTaxonomyEditor' ||
                          (selectedNode &&
                            selectedNode.isDerived &&
                            selectedNode.backend_source_path &&
                            selectedNode.include_attributes)
                        "
                      >
                        mdi-close
                      </v-icon>
                      {{ name
                      }}<template v-if="count > 1"> ({{ count }})</template>
                    </v-chip>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
          <AttributeEditorTable
            v-if="currentView === 'attributeEditor'"
            :headers="attributeTableHeaders"
            :items="filteredAttributes"
            :table-height="attributeEditorTableHeight"
            :footer-props="{
              itemsPerPageOptions: [15, 25, 50, 100],
              showFirstLastPage: true,
              firstIcon: 'mdi-page-first',
              lastIcon: 'mdi-page-last',
              prevIcon: 'mdi-chevron-left',
              nextIcon: 'mdi-chevron-right',
            }"
            :sort-by="attributeSort"
            :fullscreen="isAttributeEditorFullscreen"
            :get-display-name="getDisplayName"
            :get-values-array="getValuesArray"
            :should-display-value="shouldDisplayValue"
            :get-attr-value-chip-style="getAttributeEditorValueChipStyle"
            :remove-attr-val="removeAttributeValue"
            :renameValueFromContextMenu="renameValueFromContextMenu"
            :addNotesFromContextMenu="addNotesFromContextMenu"
            :add-attr-val="addAttributeValue"
            :toggle-expand-vals="toggleExpandValues"
            :expanded-vals="expandedAttributeValues"
            :new-val="newAttributeValue"
            :open-text-edit-dialog="openTextEditDialog"
            :update-item-values="updateItemValues"
            :get-source-chip-style="getSourceChipStyle"
            :show-cat="shouldDisplayCategory"
            :expanded-cats="expandedCategories"
            :show-value-context-menu="showValueContextMenu"
            @update:sortBy="attributeSort = $event"
            @toggleFull="toggleAttributeEditorFullscreen"
            @newAttr="openNameDialog"
            @exportCSV="exportAttributesToCSV"
            @recheckCats="recheckCategories"
            @toggleExpandCats="toggleExpandCategories"
            @deleteAttr="deleteAttribute"
          />

          <div
            v-else-if="currentView === 'languageVariants'"
            class="view-content"
          >
            <div class="attribute-management">
              <div class="sticky-header">
                <h2 class="view-title">Language Variants Editor</h2>
              </div>

              <div style="margin-bottom: 1rem; display: flex; gap: 1rem">
                <v-text-field
                  v-model="langVariantSearch"
                  label="Search by ID or English Display Name"
                  append-icon="mdi-magnify"
                  hide-details
                  clearable
                  style="max-width: 300px"
                ></v-text-field>
              </div>

              <v-data-table
                :headers="langVariantHeaders"
                :items="filteredLanguageVariantAttributes"
                item-key="attribute_id"
                :items-per-page="15"
                class="elevated"
                :footer-props="{ itemsPerPageOptions: [15, 25, 50, 100] }"
                fixed-header
                height="600px"
              >
                <template #[`item.attribute_id`]="{ item }">
                  {{ item.attribute_id }}
                </template>
                <template #[`item.display_name`]="{ item }">
                  {{ getDisplayName(item) }}
                </template>
                <template #[`item.value_count`]="{ item }">
                  {{ getValuesArray(item) ? getValuesArray(item).length : 0 }}
                </template>
                <template #[`item.actions`]="{ item }">
                  <v-btn
                    small
                    color="primary"
                    @click="openTranslationsDialog(item)"
                  >
                    Edit Translations
                  </v-btn>
                </template>
              </v-data-table>
            </div>
          </div>
          <div v-else-if="currentView === 'brands'" class="view-content">
            <BrandsEditor />
          </div>

          <div v-if="currentView === 'sizeMapping'" class="view-content">
            <SizeMapper 
              :attributeData
              :sizeMapperData
              :backendTaxonomyData
              :brandsList
              @search-brands="searchBrands"
              @data-changed="onSizeMapperDataChange" />
          </div>

          <div v-if="currentView === 'gpcMapping'" class="gpc-mapping-view">
            <div class="sticky-header">
              <h2 class="view-title">GPC Category Mapping</h2>
              <p class="text-grey-darken-1">
                Map your backend categories to Global Product Classification
                (GPC) bricks
              </p>

              <div
                class="debug-panel mb-3 pa-2"
                style="
                  background: #f5f5f5;
                  border-radius: 4px;
                  font-family: monospace;
                  font-size: 12px;
                "
              >
                <strong>Debug Info:</strong>
                Backend Categories: {{ backendCategoriesData.length }} | GPC
                Bricks: {{ gpcBricksData.length }} | Mappings:
                {{ gpcMappings.length }}
              </div>

              <div class="d-flex gap-2 mt-3">
                <v-btn
                  color="primary"
                  @click="generateBackendCategoriesFromTaxonomy"
                  :disabled="!Object.keys(taxonomyData).length"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Sync Categories from Taxonomy
                </v-btn>
              </div>
            </div>

            <div class="view-content">
              <GPCMapper
                v-if="currentView === 'gpcMapping'"
                :mappings="gpcMappings"
                :gpc-bricks-data="gpcBricksData"
                :backend-categories-data="backendCategoriesData"
                :backend-taxonomy-data="backendTaxonomyData"
                :loading="gpcDataLoading"
                :system-status="getGpcSystemStatus()"
                :current-version="loadedVersion"
                :has-unsaved-changes="hasUnsavedChanges"
                :last-publish-info="gpcLastPublishInfo"
                @mapping-changed="onGpcMappingChanged"
                @save-mappings="saveGpcMappings"
                @publish-mappings="publishGpcMappings"
                @export-mappings="exportGpcMappings"
                @import-mappings="importGpcMappings"
                @upload-gpc-data="uploadGpcBricksData"
              />
            </div>
          </div>

          <!-- Style Rules Manager View -->
          <div v-else-if="currentView === 'styleRules'" class="style-rules-view">
            <StyleRulesManager
              :rules="styleRules"
              :rulesId="styleRulesId"
              :gpc-list="gpcList"
              :segment-bricks="segmentBricks"
              :loading="styleRulesLoading"
              :system-status="getStyleRulesSystemStatus()"
              :current-version="loadedVersion"
              :has-unsaved-changes="hasUnsavedChanges"
              :last-publish-info="styleRulesLastPublishInfo"
              :brands-list="brandsList"
              :brands-loading="brandsLoading"
              :current-environment="styleRulesEnvironment"
              :taxonomy-file="selectedTaxonomy"
              :attributeData
              @rule-changed="onStyleRuleChanged"
              @export-rules="exportStyleRules"
              @import-rules="importStyleRules"
              @environment-changed="onStyleRulesEnvironmentChanged"
              @rule-updated="onStyleRuleUpdated"
              @rule-created="onStyleRuleCreated"
              @delete-rule="deleteStyleRule"
              @search-brands="searchBrands"
            />
          </div>
        </div>
      </div>

      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        timeout="2000"
        top
        right
      >
        {{ snackbar.message }}
      </v-snackbar>
      <RuleInspectorDialog
        :show="ruleInspectorDialog.show"
        :rule="ruleInspectorDialog.rule"
        @update:show="ruleInspectorDialog.show = $event"
      />
      <v-dialog v-model="attributeSearchDialog.show" max-width="500px">
        <v-card>
          <v-card-title>Select Derived Attribute</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="attributeSearchDialog.search"
              label="Search for an attribute"
              @input="filterDerivedAttributes"
              class="mb-3"
              hide-details
            ></v-text-field>

            <div
              v-if="!attributeSearchDialog.filteredAttributes.length"
              class="pa-4 text-center grey--text"
            >
              No attributes with a closed list of values were found.
            </div>

            <v-list v-else>
              <v-list-item
                v-for="attr in attributeSearchDialog.filteredAttributes"
                :key="attr.attribute_id"
                @click="selectDerivedAttribute(attr)"
              >
                <v-list-item-title>{{ attr.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="attributeSearchDialog.show = false"
              >Cancel</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="resetDialog" max-width="500px">
        <v-card>
          <v-card-title class="headline"> Confirm Reset </v-card-title>
          <v-card-text>
            <p>Are you sure you want to reset the category taxonomy?</p>
            <p class="font-weight-bold red--text">
              This will delete all category nodes and replace them with a single
              'root' node. Your attributes will not be affected. This action
              cannot be undone.
            </p>
            <p>
              You will still need to click "Save Changes" to make the reset
              permanent.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="resetDialog = false">Cancel</v-btn>
            <v-btn color="warning" text @click="resetTaxonomy">Reset</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <RuleBuilderDialog
        :show="ruleBuilderDialog.show"
        :attributes="attributeData"
        :backend-category-paths="
          allBackendCategoryPaths ? allBackendCategoryPaths : []
        "
        :backend-taxonomy-data="backendTaxonomyData"
        :editing-rule="editingFrontendRule"
        :all-web-category-codes="allWebCategoryCodes"
        @update:show="ruleBuilderDialog.show = $event"
        @save="handleSaveRule"
      />

      <v-dialog v-model="editNodeDialog" max-width="500px">
        <v-card>
          <v-card-title> Edit Node Name </v-card-title>
          <v-card-text>
            <v-text-field
              v-model="editNodeName"
              label="Node Name"
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="editNodeDialog = false">Cancel</v-btn>
            <v-btn color="primary" text @click="saveNodeName">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Unsaved Changes Warning Dialog -->
      <v-dialog v-model="unsavedChangesDialog" max-width="500px">
        <v-card>
          <v-card-title class="headline">
            <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
            Unsaved Changes Detected
          </v-card-title>
          <v-card-text>
            <p class="text-body-1 mb-3">
              You have unsaved changes that will <strong>NOT</strong> be
              included if you publish now.
            </p>
            <p class="text-body-2">What would you like to do?</p>
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="unsavedChangesDialog = false"> Cancel </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="warning"
              text
              @click="handleUnsavedChoice('publish-anyway')"
            >
              Continue to Publish Without Saving
            </v-btn>
            <v-btn color="primary" @click="handleUnsavedChoice('save-first')">
              Save & Continue to Publish
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="publishDialog" max-width="500px">
        <v-card>
          <v-card-title>Publish Taxonomy</v-card-title>
          <v-card-text>
            <p class="mb-4">
              A backup of the current version will be created before publishing.
            </p>

            <!-- Publishing scope selector -->
            <v-radio-group v-model="publishType" class="mb-4">
              <v-radio label="Current taxonomy only" value="current"></v-radio>

              <!-- Backend view options -->
              <template v-if="selectedTaxonomy !== 'frontend.json'">
                <v-radio
                  label="Current (backend) + Frontend"
                  value="both"
                  :disabled="!frontendRules || !frontendRules.categories"
                >
                </v-radio>
                <p
                  v-if="!frontendRules || !frontendRules.categories"
                  class="text-caption text-warning ml-8"
                >
                  Frontend rules not loaded - switch to frontend view once to
                  enable this option
                </p>
              </template>

              <!-- Frontend view options -->
              <template v-if="selectedTaxonomy === 'frontend.json'">
                <v-radio
                  label="Backend only"
                  value="backend"
                  :disabled="
                    !backendTaxonomyData ||
                    Object.keys(backendTaxonomyData).length === 0
                  "
                >
                </v-radio>
                <v-radio label="Frontend + Backend" value="both"> </v-radio>
              </template>
            </v-radio-group>

            <div class="d-flex gap-2">
              <v-btn
                @click="publishTo('production')"
                color="primary"
                :disabled="!publishType"
              >
                Publish to Production
              </v-btn>
              <v-btn
                @click="publishTo('test')"
                color="secondary"
                :disabled="!publishType"
              >
                Publish to Test
              </v-btn>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="publishDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Conflict Detection Dialog -->
      <v-dialog v-model="conflictDialog.show" max-width="500px" persistent>
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon color="warning" class="mr-2">mdi-alert-circle</v-icon>
            {{ conflictDialog.dataType }} Changed
          </v-card-title>
          <v-card-text>
            <p class="mb-4">
              Someone else saved changes to <strong>{{ conflictDialog.dataType }}</strong> 
              since you loaded it.
            </p>
            <v-alert type="info" variant="tonal" density="compact" class="mb-4">
              <div><strong>You loaded:</strong> {{ formatTimestamp(conflictDialog.loadedAt) }}</div>
              <div><strong>Server version:</strong> {{ formatTimestamp(conflictDialog.serverUpdatedAt) }}</div>
            </v-alert>
            <p class="text-body-2 text-grey">
              You can reload the latest version (losing your changes) or overwrite with your version.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="conflictDialog.show = false">Cancel</v-btn>
            <v-spacer></v-spacer>
            <v-btn 
              color="primary" 
              variant="outlined"
              @click="resolveConflictReload"
            >
              <v-icon start>mdi-refresh</v-icon>
              Reload Latest
            </v-btn>
            <v-btn 
              color="warning" 
              @click="resolveConflictOverwrite"
            >
              <v-icon start>mdi-content-save-alert</v-icon>
              Overwrite
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="textEditDialog.show" max-width="500px">
        <v-card>
          <v-card-title> Edit {{ textEditDialog.fieldName }} </v-card-title>
          <v-card-text>
            <v-textarea
              v-model="textEditDialog.tempValue"
              label="Value"
              rows="3"
              auto-grow
              hide-details
            ></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="textEditDialog.show = false">Cancel</v-btn>
            <v-btn color="primary" text @click="saveTextEdit">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="nameDialog" max-width="400px">
        <v-card>
          <v-card-title>Enter Attribute Name</v-card-title>
          <v-card-text>
            <v-text-field v-model="newAttributeName" label="Attribute Name" />
          </v-card-text>
          <v-card-actions>
            <v-btn @click="nameDialog = false">Cancel</v-btn>
            <v-btn @click="createAttributeWithName" color="primary"
              >Create</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="valueNotesDialog.show" max-width="500px">
        <v-card>
          <v-card-title>
            Notes for "{{ valueNotesDialog.value }}"
          </v-card-title>
          <v-card-text>
            <v-textarea
              v-model="valueNotesDialog.tempNote"
              label="Notes"
              rows="5"
              auto-grow
              hide-details
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="valueNotesDialog.show = false">Cancel</v-btn>
            <v-btn color="primary" text @click="saveValueNotes">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="translationsDialog.show" max-width="700px" persistent>
        <v-card>
          <v-card-title>
            Translations for Attribute
            {{ translationsDialog.item?.display_name.en }}
          </v-card-title>
          <v-card-text v-if="translationsDialog.item">
            <v-form>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="translationsDialog.item.display_name.de"
                    label="Display Name (German)"
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="translationsDialog.item.display_name.fr"
                    label="Display Name (French)"
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="translationsDialog.item.display_name.it"
                    label="Display Name (Italian)"
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-divider class="my-3"></v-divider>
              <div
                v-if="
                  translationsDialog.item.valuesArray &&
                  translationsDialog.item.valuesArray.length
                "
              >
                <h3 style="margin-bottom: 8px">Value Translations</h3>
                <div
                  v-for="vo in translationsDialog.item.valuesArray"
                  :key="vo.value_en"
                  class="d-flex align-center mb-2"
                >
                  <div style="width: 30%; font-weight: bold">
                    {{ vo.value_en }}
                  </div>
                  <v-text-field
                    v-model="vo.value_de"
                    label="German"
                    dense
                    style="width: 22%; margin-right: 4px"
                  />
                  <v-text-field
                    v-model="vo.value_fr"
                    label="French"
                    dense
                    style="width: 22%; margin-right: 4px"
                  />
                  <v-text-field
                    v-model="vo.value_it"
                    label="Italian"
                    dense
                    style="width: 22%"
                  />
                </div>
              </div>
              <div v-else>
                <p>No values to translate.</p>
              </div>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text color="error" @click="translationsDialog.show = false"
              >Cancel</v-btn
            >
            <v-btn color="primary" @click="closeTranslationsDialog"
              >Save Translations</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <v-dialog v-model="categoryNotesDialog.show" max-width="600px">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="amber">mdi-note-text</v-icon>
          <span>Notes for "{{ categoryNotesDialog.nodeName }}"</span>
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="categoryNotesDialog.tempNotes"
            label="Category Notes"
            placeholder="Add notes about this category (e.g., usage guidelines, review status, expansion plans)..."
            rows="8"
            auto-grow
            outlined
            counter
            maxlength="2000"
          ></v-textarea>
          <div class="text-caption mt-2 grey--text">
            These notes will be saved with the category and included when you
            save the taxonomy.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-chip
            v-if="categoryNotesDialog.notes"
            x-small
            outlined
            class="mr-2"
          >
            <v-icon x-small class="mr-1">mdi-information</v-icon>
            Has existing notes
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCategoryNotesDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="saveCategoryNotes"
            :disabled="
              categoryNotesDialog.tempNotes === categoryNotesDialog.notes
            "
          >
            Save Notes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div
      v-if="valueContextMenu.show"
      class="value-context-menu"
      :style="{
        top: valueContextMenu.y + 'px',
        left: valueContextMenu.x + 'px',
      }"
    >
      <div class="context-menu-item" @click="renameValueFromContextMenu">
        <v-icon small class="mr-2">mdi-pencil</v-icon>
        Rename Value
      </div>
      <div class="context-menu-item" @click="addNotesFromContextMenu">
        <v-icon small class="mr-2">mdi-note-text</v-icon>
        Add/Edit Notes
      </div>
    </div>

    <!-- Category Details Dialog -->
    <v-dialog v-model="categoryDetailsDialog.show" max-width="600px">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="primary">mdi-tag</v-icon>
          <span>{{ categoryDetailsDialog.categoryName }}</span>
        </v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Category Name</v-list-item-title>
                <v-list-item-subtitle>{{
                  categoryDetailsDialog.categoryName
                }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="categoryDetailsDialog.brickId">
              <v-list-item-content>
                <v-list-item-title>Brick ID</v-list-item-title>
                <v-list-item-subtitle
                  style="
                    font-family: monospace;
                    word-break: break-all;
                    background: #f5f5f5;
                    padding: 4px 8px;
                    border-radius: 4px;
                    margin-top: 4px;
                  "
                >
                  {{ categoryDetailsDialog.brickId }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  icon
                  small
                  @click="copyToClipboard(categoryDetailsDialog.brickId)"
                  :title="'Copy Brick ID'"
                >
                  <v-icon small>mdi-content-copy</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>

            <v-list-item v-else>
              <v-list-item-content>
                <v-list-item-title>Brick ID</v-list-item-title>
                <v-list-item-subtitle>
                  <v-chip size="small" color="warning" variant="tonal">
                    <v-icon start size="small">mdi-alert-circle</v-icon>
                    No mapping available
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Status</v-list-item-title>
                <v-list-item-subtitle>
                  <v-chip
                    size="small"
                    :color="
                      categoryDetailsDialog.brickId ? 'success' : 'warning'
                    "
                    variant="tonal"
                  >
                    <v-icon
                      start
                      size="small"
                      :icon="
                        categoryDetailsDialog.brickId
                          ? 'mdi-check-circle'
                          : 'mdi-alert-circle'
                      "
                    ></v-icon>
                    {{ categoryDetailsDialog.brickId ? "Mapped" : "Unmapped" }}
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="categoryDetailsDialog.show = false">Close</v-btn>
          <v-btn
            color="primary"
            @click="selectCategoryFromDialog"
            v-if="categoryDetailsDialog.categoryName !== selectedTopLevelNode"
          >
            Select Category
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- ============================================================================ -->
    <!-- V2 TAXONOMY DIALOGS -->
    <!-- ============================================================================ -->
    
    <!-- Create V2 Dialog -->
    <v-dialog v-model="showCreateV2Dialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          Create Taxonomy V2
        </v-card-title>
        
        <v-card-text>
          <p class="mb-4">
            This will create a new V2 taxonomy based on the current V1 structure.
          </p>
          
          <v-text-field
            v-model="newV2Filename"
            label="V2 Filename"
            :placeholder="getSuggestedV2Filename()"
            hint="Leave empty to use suggested name"
            persistent-hint
            class="mb-4"
          />
          
          <v-checkbox
            v-model="createV2Options.copyFrontend"
            label="Copy frontend rules (will be orphaned initially)"
            hint="Frontend rules will reference V1 categories until you build V2 structure"
            persistent-hint
            class="mb-2"
          />
          
          <v-checkbox
            v-model="createV2Options.copyGpcMappings"
            label="Copy GPC mappings"
            hint="Usually not recommended - V2 may have different category structure"
            persistent-hint
          />
          
          <v-alert type="info" variant="tonal" class="mt-4">
            <strong>Attributes are shared</strong> - they will automatically be available in V2.
            No need to copy or duplicate them.
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showCreateV2Dialog = false">Cancel</v-btn>
          <v-btn 
            color="primary" 
            :loading="creatingV2"
            @click="handleCreateV2"
          >
            Create V2
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Orphan Report Dialog -->
    <v-dialog v-model="showOrphanReportDialog" max-width="700">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2" color="warning">mdi-alert-circle</v-icon>
          Orphaned Frontend Rules
        </v-card-title>
        
        <v-card-text v-if="orphanReport">
          <div class="d-flex align-center justify-space-between mb-4">
            <div>
              <div class="text-h6">{{ orphanReport.summary.orphaned }} Orphaned</div>
              <div class="text-caption">out of {{ orphanReport.summary.total }} total rules</div>
            </div>
            <div>
              <v-progress-circular
                :model-value="orphanReport.summary.percentResolved"
                :size="60"
                :width="8"
                color="success"
              >
                {{ orphanReport.summary.percentResolved }}%
              </v-progress-circular>
            </div>
          </div>
          
          <v-divider class="mb-4" />
          
          <div class="text-subtitle-2 mb-2">Orphaned Rules</div>
          <v-list density="compact" class="orphan-list" style="max-height: 300px; overflow-y: auto;">
            <v-list-item
              v-for="orphan in orphanReport.orphanedNodes"
              :key="orphan.frontendNodeId"
            >
              <template v-slot:prepend>
                <v-icon color="warning" size="small">mdi-link-off</v-icon>
              </template>
              <v-list-item-title>{{ orphan.frontendNodeName }}</v-list-item-title>
              <v-list-item-subtitle>
                Missing: {{ orphan.backendCategoryId }}
                <br>
                <span class="text-caption">Original V1 path: {{ orphan.originalBackendPath }}</span>
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
          
          <v-alert type="info" variant="tonal" class="mt-4">
            To resolve orphans, create matching categories in your V2 taxonomy structure.
            The frontend rules will automatically link once the backend categories exist.
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showOrphanReportDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Shared Attributes Warning Banner (shown when editing attributes) -->
    <v-snackbar
      v-model="showSharedAttributesWarning"
      :timeout="-1"
      location="top"
      color="warning"
      multi-line
    >
      <div class="d-flex align-center">
        <v-icon class="mr-3">mdi-alert</v-icon>
        <div>
          <div class="font-weight-bold">Shared Attributes Mode</div>
          <div class="text-body-2">
            You are editing the shared attribute library. Changes will affect 
            <strong>all taxonomy versions</strong> (V1 and V2).
          </div>
        </div>
      </div>
      <template v-slot:actions>
        <v-btn variant="text" @click="sharedAttributesWarningDismissed = true; showSharedAttributesWarning = false">
          Got it
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>
<script>
import componentOptions from "@/utils/TaxonomyEditor.js";
export default {
  ...componentOptions,
};
</script>

<style>
@import "@/styles/components/TaxonomyEditor.css";
</style>
